import { useEffect } from "react";
import { useLocation } from "react-router";
import { resolvePageSeo } from "@/lib/seo";

const SITE_ORIGIN = "https://askcarmen.eu";
const SOCIAL_IMAGE_URL = `${SITE_ORIGIN}/favicon.png`;
const STRUCTURED_DATA_SELECTOR = 'script[type="application/ld+json"]';
const initialStructuredData = document.head.querySelector<HTMLScriptElement>(STRUCTURED_DATA_SELECTOR)?.textContent;

function setMetaContent(selector: string, attributeName: string, attributeValue: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attributeName, attributeValue);
    document.head.append(element);
  }
  element.content = content;
}

function setLinkHref(selector: string, rel: string, href: string, type?: string) {
  let element = document.head.querySelector<HTMLLinkElement>(selector);
  if (!element) {
    element = document.createElement("link");
    element.rel = rel;
    if (type) element.type = type;
    document.head.append(element);
  }
  element.href = href;
}

function removeHeadElements(selector: string) {
  document.head.querySelectorAll(selector).forEach((element) => element.remove());
}

function setStructuredData(enabled: boolean) {
  const existing = document.head.querySelector<HTMLScriptElement>(STRUCTURED_DATA_SELECTOR);
  if (!enabled) {
    existing?.remove();
    return;
  }

  if (!existing && initialStructuredData) {
    const element = document.createElement("script");
    element.type = "application/ld+json";
    element.textContent = initialStructuredData;
    document.head.append(element);
  }
}

export function SeoMetadata() {
  const location = useLocation();

  useEffect(() => {
    const page = resolvePageSeo(location.pathname);
    const canonicalUrl = `${SITE_ORIGIN}${page.canonicalPath === "/" ? "/" : page.canonicalPath}`;

    document.title = page.title;
    document.documentElement.lang = "nl-BE";

    setMetaContent('meta[name="description"]', "name", "description", page.description);
    setMetaContent(
      'meta[name="robots"]',
      "name",
      "robots",
      page.indexable ? "index, follow, max-image-preview:large" : "noindex, nofollow",
    );
    if (page.indexable) {
      setMetaContent('meta[property="og:type"]', "property", "og:type", "website");
      setMetaContent('meta[property="og:locale"]', "property", "og:locale", "nl_BE");
      setMetaContent('meta[property="og:site_name"]', "property", "og:site_name", "Carmen");
      setMetaContent('meta[property="og:title"]', "property", "og:title", page.title);
      setMetaContent('meta[property="og:description"]', "property", "og:description", page.description);
      setMetaContent('meta[property="og:url"]', "property", "og:url", canonicalUrl);
      setMetaContent('meta[property="og:image"]', "property", "og:image", SOCIAL_IMAGE_URL);
      setMetaContent('meta[name="twitter:card"]', "name", "twitter:card", "summary");
      setMetaContent('meta[name="twitter:title"]', "name", "twitter:title", page.title);
      setMetaContent('meta[name="twitter:description"]', "name", "twitter:description", page.description);
      setMetaContent('meta[name="twitter:image"]', "name", "twitter:image", SOCIAL_IMAGE_URL);
      setLinkHref('link[rel="canonical"]', "canonical", canonicalUrl);
      setLinkHref(
        'link[rel="describedby"][type="text/plain"]',
        "describedby",
        `${SITE_ORIGIN}/llms.txt`,
        "text/plain",
      );

      const markdownPath = page.canonicalPath === "/" ? "/index.md" : `${page.canonicalPath}.md`;
      setLinkHref(
        'link[rel="alternate"][type="text/markdown"]',
        "alternate",
        `${SITE_ORIGIN}${markdownPath}`,
        "text/markdown",
      );
      setStructuredData(true);
    } else {
      removeHeadElements('meta[property^="og:"], meta[name^="twitter:"]');
      removeHeadElements(
        'link[rel="canonical"], link[rel="alternate"][type="text/markdown"], link[rel="describedby"]',
      );
      setStructuredData(false);
    }
  }, [location.pathname]);

  return null;
}
